/**
 * Conversational search powered by Shopify's Storefront MCP servers.
 *
 * - Product discovery goes through the UCP catalog endpoint (/api/ucp/mcp,
 *   search_catalog) — Shopify's semantic search, which accepts natural
 *   language plus a conversational `context.intent` for follow-up questions.
 * - Store questions (shipping, returns, etc.) go through the standard
 *   Storefront MCP endpoint (/api/mcp, search_shop_policies_and_faqs).
 * - Result cards are hydrated from /products/{handle}.js so images and
 *   live prices match the storefront.
 *
 * All API text is rendered via textContent — never innerHTML — since MCP
 * responses are external data.
 */
(() => {
  const UCP_ENDPOINT = '/api/ucp/mcp';
  const STANDARD_ENDPOINT = '/api/mcp';
  const MAX_INTENT_LENGTH = 600;

  const QUESTION_PATTERN = /^(who|what|when|where|why|how|can|could|do|does|did|is|are|will|would|should)\b|\?/i;
  const POLICY_PATTERN = /\b(polic\w*|ship\w*|deliver\w*|return\w*|refund\w*|exchange\w*|guarantee\w*|warrant\w*|cancel\w*|subscri\w*|discount\w*|coupon\w*|promo\w*|payment\w*|contact|support|hours)\b/i;
  const SHOW_MORE_PATTERN = /^(show|see|load|give)?\s*(me)?\s*more( (results|products|options))?\s*$/i;

  class AISearch extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.log = this.querySelector('[data-log]');
      this.form = this.querySelector('[data-form]');
      this.input = this.querySelector('[data-input]');
      this.submitButton = this.form?.querySelector('[type="submit"]');
      this.cardTemplate = this.querySelector('template[data-card-template]');

      this.profileUrl = this.normalizeUrl(this.dataset.profileUrl);
      this.limit = parseInt(this.dataset.limit, 10) || 4;
      this.askPolicies = this.dataset.askPolicies === 'true';
      this.showAddToCart = this.dataset.showAddToCart === 'true';
      this.strings = this.readStrings();

      this.history = [];
      this.lastSearch = null;
      this.busy = false;

      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit(this.input.value);
      });

      this.querySelectorAll('[data-suggestion]').forEach((button) => {
        button.addEventListener('click', () => this.submit(button.dataset.suggestion || button.textContent));
      });
    }

    readStrings() {
      let parsed = {};
      try {
        parsed = JSON.parse(this.querySelector('script[data-strings]')?.textContent || '{}');
      } catch (error) {
        // Fall through to defaults.
      }
      return {
        thinking: parsed.thinking || 'Looking that up…',
        foundProducts: parsed.foundProducts || 'Here’s what I found:',
        noResults: parsed.noResults || 'I couldn’t find a match for that. Try describing what you’re looking for another way.',
        error: parsed.error || 'Something went wrong on my end. Please try again.',
        showMore: parsed.showMore || 'Show more',
        addToCart: parsed.addToCart || 'Add to cart',
        added: parsed.added || 'Added!',
        viewProduct: parsed.viewProduct || 'View product',
        soldOut: parsed.soldOut || 'Sold out'
      };
    }

    // UCP discovery fetches the agent profile server-side, so it must be an
    // absolute https URL. asset_url emits protocol-relative URLs, and the CLI's
    // theme dev proxy rewrites them to root-relative — resolve both against a
    // host Shopify can reach (the myshopify domain when available).
    normalizeUrl(url) {
      if (!url) return null;
      if (url.startsWith('//')) return `https:${url}`;
      if (url.startsWith('/')) {
        const host = window.Shopify?.shop || window.location.host;
        return `https://${host}${url}`;
      }
      return url;
    }

    submit(rawQuery) {
      const query = (rawQuery || '').trim();
      if (!query || this.busy) return;

      this.input.value = '';
      this.appendBubble('user', query);
      this.hideSuggestions();

      if (SHOW_MORE_PATTERN.test(query) && this.lastSearch?.cursor) {
        this.runSearch(this.lastSearch.query, this.lastSearch.intent, this.lastSearch.cursor);
        return;
      }

      const intent = this.buildIntent();
      this.history.push(query);
      this.runSearch(query, intent, null);
    }

    buildIntent() {
      if (!this.history.length) return null;
      let intent = `Earlier in this conversation the shopper said: ${this.history.join('; ')}`;
      if (intent.length > MAX_INTENT_LENGTH) intent = `…${intent.slice(intent.length - MAX_INTENT_LENGTH)}`;
      return intent;
    }

    async runSearch(query, intent, cursor) {
      this.setBusy(true);
      const thinking = this.appendBubble('assistant', this.strings.thinking, 'ai-search__bubble--thinking');

      try {
        const wantsPolicies = this.askPolicies && !cursor && (QUESTION_PATTERN.test(query) || POLICY_PATTERN.test(query));
        const [catalog, faqs] = await Promise.all([
          this.searchCatalog(query, intent, cursor),
          wantsPolicies ? this.searchPolicies(query).catch(() => null) : Promise.resolve(null)
        ]);

        thinking.remove();

        if (faqs?.length) this.renderFaqs(faqs);

        const products = catalog?.products || [];
        if (products.length) {
          if (!cursor) this.appendBubble('assistant', this.strings.foundProducts);
          await this.renderProducts(products);
          this.lastSearch = {
            query,
            intent,
            cursor: catalog?.pagination?.has_next_page ? catalog.pagination.cursor : null
          };
          this.renderShowMore();
        } else if (!faqs?.length) {
          this.appendBubble('assistant', this.strings.noResults);
        }
      } catch (error) {
        console.error('[ai-search]', error);
        thinking.remove();
        this.appendBubble('assistant', this.strings.error);
      } finally {
        this.setBusy(false);
      }
    }

    async searchCatalog(query, intent, cursor) {
      const catalog = {
        query,
        pagination: { limit: this.limit }
      };
      if (cursor) catalog.pagination.cursor = cursor;

      const context = {};
      if (this.dataset.country) context.address_country = this.dataset.country;
      if (this.dataset.language) context.language = this.dataset.language;
      if (this.dataset.currency) context.currency = this.dataset.currency;
      if (intent) context.intent = intent;
      if (Object.keys(context).length) catalog.context = context;

      const args = { catalog };
      if (this.profileUrl) args.meta = { 'ucp-agent': { profile: this.profileUrl } };

      return this.callTool(UCP_ENDPOINT, 'search_catalog', args, (payload) => Array.isArray(payload?.products));
    }

    async searchPolicies(query) {
      const payload = await this.callTool(STANDARD_ENDPOINT, 'search_shop_policies_and_faqs', { query }, (data) => Array.isArray(data));
      return Array.isArray(payload) ? payload.filter((item) => item?.answer) : null;
    }

    // /api/ucp/mcp sends `Access-Control-Allow-Origin: *`, so target the
    // myshopify domain directly — works on the live domain, in the Theme
    // Editor, and through the CLI's `theme dev` proxy (which doesn't forward
    // /api POSTs). /api/mcp has no CORS headers and must stay same-origin.
    resolveEndpoint(endpoint) {
      if (endpoint !== UCP_ENDPOINT) return endpoint;
      return window.Shopify?.shop ? `https://${window.Shopify.shop}${endpoint}` : endpoint;
    }

    async callTool(endpoint, name, args, matcher) {
      const response = await fetch(this.resolveEndpoint(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: Date.now(),
          method: 'tools/call',
          params: { name, arguments: args }
        })
      });
      if (!response.ok) throw new Error(`${name} failed: HTTP ${response.status}`);

      const data = await response.json();
      if (data.error) throw new Error(`${name} failed: ${data.error.message}`);
      if (data.result?.isError) throw new Error(`${name} returned an error result`);

      for (const item of data.result?.content || []) {
        if (item.type !== 'text') continue;
        try {
          const parsed = JSON.parse(item.text);
          if (matcher(parsed)) return parsed;
        } catch (error) {
          // Not JSON (e.g. deprecation notices) — skip.
        }
      }
      return null;
    }

    /* ---------- Rendering ---------- */

    appendBubble(role, text, extraClass) {
      const bubble = document.createElement('div');
      bubble.className = `ai-search__bubble ai-search__bubble--${role}${extraClass ? ` ${extraClass}` : ''}`;
      bubble.textContent = text;
      this.log.appendChild(bubble);
      this.scrollToEnd();
      return bubble;
    }

    renderFaqs(faqs) {
      const bubble = document.createElement('div');
      bubble.className = 'ai-search__bubble ai-search__bubble--assistant';
      faqs.slice(0, 3).forEach((faq) => {
        const answer = document.createElement('p');
        answer.textContent = faq.answer;
        bubble.appendChild(answer);
      });
      this.log.appendChild(bubble);
      this.scrollToEnd();
    }

    async renderProducts(products) {
      const grid = document.createElement('div');
      grid.className = 'ai-search__products';

      const hydrated = await Promise.all(products.map((product) => this.hydrate(product)));
      hydrated.forEach((product) => grid.appendChild(this.renderCard(product)));

      this.log.appendChild(grid);
      this.scrollToEnd();
    }

    // Enrich an MCP catalog product with the storefront's own product JSON
    // (images and live prices aren't part of the UCP search payload).
    async hydrate(product) {
      const result = {
        handle: product.handle,
        title: product.title,
        url: product.handle ? `/products/${product.handle}` : null,
        image: null,
        price: product.price_range?.min?.amount,
        compareAtPrice: null,
        currency: product.price_range?.min?.currency,
        available: product.variants?.some((variant) => variant.availability?.available) ?? true,
        variantId: null
      };

      const variants = product.variants || [];
      if (variants.length === 1 && variants[0].id) {
        result.variantId = variants[0].id.split('/').pop();
      }

      if (!product.handle) return result;

      try {
        const response = await fetch(`/products/${product.handle}.js`);
        if (!response.ok) return result;
        const json = await response.json();
        result.title = json.title || result.title;
        result.url = json.url || result.url;
        result.image = json.featured_image;
        result.price = json.price;
        result.compareAtPrice = json.compare_at_price > json.price ? json.compare_at_price : null;
        result.available = json.available;
        if (json.variants?.length === 1) result.variantId = json.variants[0].id;
        if (json.variants?.length > 1) result.variantId = null;
      } catch (error) {
        // Offline/password page — fall back to MCP data.
      }
      return result;
    }

    renderCard(product) {
      const card = document.createElement('div');
      card.className = 'ai-search__card';

      const link = document.createElement('a');
      link.className = 'ai-search__card-media';
      if (product.url) link.href = product.url;
      if (product.image) {
        const image = document.createElement('img');
        image.src = this.resizeImage(product.image, 360);
        image.alt = product.title || '';
        image.loading = 'lazy';
        image.width = 180;
        image.height = 180;
        link.appendChild(image);
      }
      card.appendChild(link);

      const body = document.createElement('div');
      body.className = 'ai-search__card-body';

      const title = document.createElement('a');
      title.className = 'ai-search__card-title';
      if (product.url) title.href = product.url;
      title.textContent = product.title || '';
      body.appendChild(title);

      if (typeof product.price === 'number') {
        const price = document.createElement('p');
        price.className = 'ai-search__card-price';
        price.appendChild(this.formatPriceNode(product.price, product.compareAtPrice, product.currency));
        body.appendChild(price);
      }

      body.appendChild(this.renderCardAction(product));
      card.appendChild(body);
      return card;
    }

    renderCardAction(product) {
      if (!product.available) {
        const soldOut = document.createElement('span');
        soldOut.className = 'ai-search__card-status';
        soldOut.textContent = this.strings.soldOut;
        return soldOut;
      }

      if (this.showAddToCart && product.variantId) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ai-search__card-button button button--primary';
        button.textContent = this.strings.addToCart;
        button.addEventListener('click', () => this.addToCart(product.variantId, button));
        return button;
      }

      const view = document.createElement('a');
      view.className = 'ai-search__card-button button button--secondary';
      if (product.url) view.href = product.url;
      view.textContent = this.strings.viewProduct;
      return view;
    }

    renderShowMore() {
      this.querySelector('[data-show-more]')?.remove();
      if (!this.lastSearch?.cursor) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'ai-search__more';
      wrapper.setAttribute('data-show-more', '');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ai-search__more-button';
      button.textContent = this.strings.showMore;
      button.addEventListener('click', () => {
        wrapper.remove();
        this.runSearch(this.lastSearch.query, this.lastSearch.intent, this.lastSearch.cursor);
      });

      wrapper.appendChild(button);
      this.log.appendChild(wrapper);
      this.scrollToEnd();
    }

    /* ---------- Cart ---------- */

    addToCart(variantId, button) {
      const routes = window.theme?.routes || {};
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

      const config = window.theme?.utils?.fetchConfig
        ? { ...window.theme.utils.fetchConfig('javascript') }
        : { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };
      config.body = JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionsToBundle });

      button.setAttribute('aria-busy', 'true');
      fetch(routes.cart_add_url || '/cart/add.js', config)
        .then((response) => response.json())
        .then(async (parsedState) => {
          if (parsedState.status) throw new Error(parsedState.description || 'Add to cart failed');

          const cartJson = await (await fetch(routes.cart_url || '/cart.js', window.theme?.utils?.fetchConfig ? { ...window.theme.utils.fetchConfig('json', 'GET') } : undefined)).json();
          cartJson.sections = parsedState.sections;
          window.theme?.pubsub?.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'ai-search', cart: cartJson });

          button.textContent = this.strings.added;
          setTimeout(() => { button.textContent = this.strings.addToCart; }, 2000);
          document.querySelector('cart-drawer')?.show?.(button);
        })
        .catch((error) => {
          console.error('[ai-search]', error);
          this.appendBubble('assistant', this.strings.error);
        })
        .finally(() => button.removeAttribute('aria-busy'));
    }

    /* ---------- Helpers ---------- */

    formatPriceNode(price, compareAtPrice, currency) {
      const fragment = document.createDocumentFragment();
      const current = document.createElement('span');
      current.textContent = this.formatMoney(price, currency);
      fragment.appendChild(current);

      if (compareAtPrice) {
        const compare = document.createElement('s');
        compare.className = 'ai-search__card-compare';
        compare.textContent = this.formatMoney(compareAtPrice, currency);
        fragment.appendChild(compare);
      }
      return fragment;
    }

    formatMoney(amount, currency) {
      if (window.theme?.Currency?.formatMoney && window.theme?.settings?.moneyFormat) {
        const formatted = window.theme.Currency.formatMoney(amount, window.theme.settings.moneyFormat);
        const span = document.createElement('span');
        span.innerHTML = formatted;
        return span.textContent;
      }
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount / 100);
      } catch (error) {
        return `$${(amount / 100).toFixed(2)}`;
      }
    }

    resizeImage(url, width) {
      try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set('width', width);
        return parsed.toString();
      } catch (error) {
        return url;
      }
    }

    hideSuggestions() {
      this.querySelector('[data-suggestions]')?.remove();
    }

    setBusy(busy) {
      this.busy = busy;
      this.input.toggleAttribute('disabled', busy);
      this.submitButton?.toggleAttribute('disabled', busy);
      if (!busy) this.input.focus();
    }

    scrollToEnd() {
      this.log.scrollTop = this.log.scrollHeight;
    }
  }

  if (!customElements.get('ai-search')) {
    customElements.define('ai-search', AISearch);
  }
})();
